import { liveEligible } from './selectors';
export const symbolSnapshot = (symbols) => Object.freeze([...new Set(symbols)].sort());
export const sameSymbols = (a, b) => a.length === b.length && a.every((symbol, i) => symbol === b[i]);
export const covers = (snapshot, symbols) => symbols.every((symbol) => snapshot.includes(symbol));
export const matches = (row, symbols, adapter) => adapter.symbolsOf(row).some((symbol) => symbols.includes(symbol));
export function mergeRows(old, incoming, adapter) {
    const rows = new Map(old.map((row) => [adapter.keyOf(row), row]));
    for (const row of incoming)
        rows.set(adapter.keyOf(row), row);
    return [...rows.values()].sort(adapter.compare);
}
/** Reconcile all retained collections together so moving a key never renews its age. */
export function classifyRows(state, now, sweep, adapter) {
    const timestamps = new Map();
    const classify = (rows) => rows.filter((row) => {
        const key = adapter.keyOf(row);
        if (matches(row, state.activeSymbols, adapter))
            return true;
        const timestamp = state.unmatchedAt.get(key) ?? now;
        if (state.source.retention === 0 || (sweep && now - timestamp >= state.source.retention))
            return false;
        timestamps.set(key, timestamp);
        return true;
    });
    const rows = classify(state.rows);
    const windowKeys = new Set(rows.map(adapter.keyOf));
    const leadRows = classify(state.lead.rows.filter((row) => !windowKeys.has(adapter.keyOf(row))));
    const refresh = state.lead.refresh.status === 'paging'
        ? {
            ...state.lead.refresh,
            staging: classify(state.lead.refresh.staging.filter((row) => !windowKeys.has(adapter.keyOf(row)))),
        }
        : state.lead.refresh;
    const anchorIndex = rows.findIndex((row) => state.parity.has(adapter.keyOf(row)));
    const anchorParity = anchorIndex === -1 ? 'odd' : state.parity.get(adapter.keyOf(rows[anchorIndex]));
    const origin = Math.max(0, anchorIndex);
    const parity = new Map(rows.map((row, index) => [
        adapter.keyOf(row),
        ((index - origin) % 2 === 0 ? anchorParity : anchorParity === 'odd' ? 'even' : 'odd'),
    ]));
    const pagedRowKeys = new Set([...state.pagedRowKeys].filter((key) => windowKeys.has(key)));
    return { ...state, rows, unmatchedAt: timestamps, parity, pagedRowKeys, lead: { rows: leadRows, refresh } };
}
export function leadDue(state) {
    const refresh = state.lead.refresh.status === 'paging'
        ? { ...state.lead.refresh, refreshQueued: true }
        : { status: 'due' };
    return { ...state, lead: { ...state.lead, refresh } };
}
export function reopenInitial(state) {
    return {
        ...state,
        initial: 'uncommitted',
        caughtUp: false,
        reachedEnd: false,
        liveCatchUp: 'inactive',
        liveTopRecovery: false,
        liveDue: false,
    };
}
export function pruneMetadata(state, adapter) {
    const keys = new Set([
        ...state.rows,
        ...state.lead.rows,
        ...(state.lead.refresh.status === 'paging' ? state.lead.refresh.staging : []),
    ].map(adapter.keyOf));
    return { ...state, unmatchedAt: new Map([...state.unmatchedAt].filter(([key]) => keys.has(key))) };
}
/** External removal invalidates edges and useless frozen passes before the driver can abort. */
export function afterEviction(before, state, adapter, config) {
    const keys = new Set(state.rows.map(adapter.keyOf));
    const lostTop = before.rows.length > 0 && !keys.has(adapter.keyOf(before.rows[0]));
    const lostBottom = before.rows.length > 0 && !keys.has(adapter.keyOf(before.rows[before.rows.length - 1]));
    let next = state;
    if (lostTop) {
        next = {
            ...next,
            caughtUp: false,
            liveCatchUp: 'inactive',
            liveTopRecovery: liveEligible(before, config),
            liveDue: false,
        };
    }
    if (lostBottom)
        next = { ...next, reachedEnd: false };
    const backfillUseless = next.backfill.status === 'paging' &&
        !next.backfill.entries.some((entry) => next.roundSymbols.includes(entry.symbol));
    const leadUseless = next.lead.refresh.status === 'paging' &&
        !next.lead.refresh.symbols.some((symbol) => next.roundSymbols.includes(symbol));
    if (next.fetch.status !== 'idle') {
        const kind = next.fetch.instruction.kind;
        if ((kind === 'up' && lostTop) ||
            (kind === 'down' && lostBottom) ||
            (kind === 'backfill' && backfillUseless) ||
            (kind === 'lead' && leadUseless)) {
            next = { ...next, fetch: { status: 'idle' } };
        }
    }
    if (backfillUseless)
        next = { ...next, backfill: { status: 'none' } };
    if (leadUseless)
        next = { ...next, lead: { ...next.lead, refresh: { status: 'due' } } };
    if (before.rows.length > 0 &&
        next.rows.length === 0 &&
        next.roundSymbols.length > 0 &&
        next.fetch.status !== 'failed') {
        next = reopenInitial(next);
    }
    return backfillUseless || leadUseless ? pruneMetadata(next, adapter) : next;
}
//# sourceMappingURL=rows.js.map